/**
 * Payee detail KPI totals (AB#6872): "Paid in {year}", "Bills" and "Waiting to be paid".
 * Paid and failed come from the shared bill-bucket rule (bucketFor), the same one the
 * Dashboard, Bills and Bill detail use, so the tiles agree with every status chip.
 *
 * PURE module: no React, MUI, axios or auth imports, so it is unit-testable.
 */
import { bucketFor } from "../../utils/billBucket";
import type { BillBucket } from "../../utils/billBucket";

/** The bill fields the totals read. Everything is optional because bill data is uneven. */
export interface PayeeTotalsBill {
  amount?: number | null;
  status?: string | null;
  paymentService?: {
    paymentStatus?: string | null;
    paymentDate?: string | Date | null;
  } | null;
}

export interface PayeeTotals {
  /** Sum of bills in the 'paid' bucket whose payment date falls in `year`. */
  paidInYear: number;
  /** Every bill, whatever its status. */
  billCount: number;
  /** Sum of bills in any bucket other than 'paid' and 'failed'. */
  waitingToBePaid: number;
}

/** Tax years follow the Eastern calendar year, like the server's 1099 rule. */
export const PAYEE_TOTALS_TIME_ZONE = "America/New_York";

const amountOf = (bill: PayeeTotalsBill): number =>
  typeof bill.amount === "number" && Number.isFinite(bill.amount)
    ? bill.amount
    : 0;

/**
 * Calendar year of `date` in `timeZone` (IANA name), or null when the date is missing or
 * invalid. Dec 31 23:30 in New York is still that year there, though it is Jan 1 in UTC.
 */
export function yearInTimeZone(
  date: string | Date | null | undefined,
  timeZone: string = PAYEE_TOTALS_TIME_ZONE
): number | null {
  if (date === null || date === undefined) return null;
  if (typeof date === "string" && date.trim() === "") return null;
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
  }).format(parsed);
  const n = parseInt(year, 10);
  return Number.isNaN(n) ? null : n;
}

/** The bill's bucket: bill status wins; payment status only for a legacy bill without one. */
export function payeeBillBucket(bill: PayeeTotalsBill): BillBucket {
  return bucketFor(bill.status, bill.paymentService?.paymentStatus);
}

/** A bill is paid when the shared rule puts it in the 'paid' bucket. */
export function isPaidBill(bill: PayeeTotalsBill): boolean {
  return payeeBillBucket(bill) === "paid";
}

/** A bill is failed when the shared rule puts it in the 'failed' bucket. */
export function isFailedBill(bill: PayeeTotalsBill): boolean {
  return payeeBillBucket(bill) === "failed";
}

/**
 * Totals for the payee detail KPI tiles, computed from ALL of the payee's bills.
 * - paidInYear: bucket 'paid' and payment date in `year` in `timeZone`.
 * - billCount: number of bills.
 * - waitingToBePaid: bills in any bucket other than 'paid' and 'failed' (not sent yet,
 *   sent, or deferred).
 */
export function payeeTotals(
  bills: ReadonlyArray<PayeeTotalsBill> | null | undefined,
  year: number,
  timeZone: string = PAYEE_TOTALS_TIME_ZONE
): PayeeTotals {
  const list = bills ?? [];
  let paidInYear = 0;
  let waitingToBePaid = 0;

  list.forEach((bill) => {
    if (!bill) return;
    const amount = amountOf(bill);
    const bucket = payeeBillBucket(bill);
    if (bucket === "paid") {
      if (yearInTimeZone(bill.paymentService?.paymentDate, timeZone) === year) {
        paidInYear += amount;
      }
    } else if (bucket !== "failed") {
      waitingToBePaid += amount;
    }
  });

  return {
    paidInYear: Math.round(paidInYear * 100) / 100,
    billCount: list.length,
    waitingToBePaid: Math.round(waitingToBePaid * 100) / 100,
  };
}
